/**
 * phase-ui-company-profiles-improvements (Item 4) — the field-usage ⓘ popover,
 * extracted from CompanyProfilePage by phase-profile-contact-rows so the
 * contact/signatory ROW lists can render the same popover from the same
 * context. One implementation, two call sites — no duplicated presentation.
 */
import {
  Fragment,
  cloneElement,
  isValidElement,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { FieldUsageContext } from './fieldUsageContext';
import type {
  CompanyProfileFieldConsumer,
  CompanyProfileFieldUsage,
} from '@/api/v2Client';

// phase-ui-company-profiles-improvements (Item 4) — field-usage context.
// The modal fetches the derived map once and provides it; each Field with a
// `column` renders an ⓘ whose tooltip lists the real consumers. Derived
// server-side from the resolver + aliases + templates + form mappings — see
// src/services/companyProfileFieldUsage.ts. No hand-maintained list here.
export { FieldUsageContext } from './fieldUsageContext';

// The consumer groups, in display order (Templates first, then Government
// forms). phase-ui-field-usage-popover-formatting.
const USAGE_GROUPS: ReadonlyArray<{ kind: CompanyProfileFieldConsumer['kind']; label: string }> = [
  { kind: 'template', label: 'Templates' },
  { kind: 'govForm', label: 'Government forms' },
];

// phase-fix-field-usage-tooltip-render — a real info glyph. The previous
// implementation used the unicode character ⓘ (U+24D8), which fonts without
// that codepoint render as a tofu "?" (Sharon's screenshot). An inline SVG is
// font-independent, so it renders identically everywhere.
function InfoGlyph(): ReactElement {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block' }}
    >
      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="8" cy="4.6" r="1" fill="currentColor" />
      <rect x="7.1" y="6.9" width="1.8" height="4.8" rx="0.9" fill="currentColor" />
    </svg>
  );
}

/**
 * phase-fix-field-usage-tooltip-render — the field-usage popover.
 *
 * The previous version put the usage text in a native `title=""` attribute on a
 * non-interactive `<span>` nested inside the field's `<label>`. Three failures
 * shipped together (unit tests only ever read the `title` attribute, so none
 * were caught): the ⓘ glyph tofu'd to "?"; a native `title` never appears on
 * click and its `\n`s don't render; and clicking a non-interactive span inside a
 * `<label>` fires the label's default action — focusing the wrapped input.
 *
 * This is a real popover: a focusable `<button>` (so clicking it does NOT
 * activate the label's control), an inline-SVG glyph, and a positioned popover
 * that opens on hover AND click (click pins it; ESC / outside-click / blur
 * close it). Keyboard accessible via aria-expanded + aria-describedby.
 */
/** phase-ui-field-usage-popover-formatting — a compact REQUIRED pill. */
function RequiredBadge(): ReactElement {
  return (
    <span
      style={{
        flexShrink: 0,
        marginLeft: 'auto',
        alignSelf: 'center',
        padding: '1px 6px',
        borderRadius: 10,
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: '0.03em',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        background: 'var(--amber-bg, #fef3c7)',
        color: 'var(--amber, #b45309)',
        border: '1px solid var(--amber, #f59e0b)',
      }}
      title="Generation blocks if this field is empty"
    >
      REQUIRED
    </span>
  );
}

/**
 * phase-ui-field-usage-popover-formatting — the popover body as a readable
 * list. A heavily-used field (e.g. Company name, ~15 consumers) previously
 * rendered as an unbroken wall of `pre-line` text (Sharon 2026-07-17). Now the
 * already-fetched usage data is rendered as rows: grouped under "Templates" /
 * "Government forms" headers, zebra-shaded, each row showing the consumer name
 * with its variable in muted text and a compact REQUIRED badge, capped at a
 * max height with internal scroll, above a muted coverage footer.
 */
function UsageContent({
  usage,
  coverage,
}: {
  usage: CompanyProfileFieldUsage | undefined;
  coverage: string;
}): ReactElement {
  const hasConsumers = !!usage && usage.consumers.length > 0;

  return (
    <>
      {hasConsumers ? (
        <div
          data-testid="field-usage-rows"
          style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
        >
          {USAGE_GROUPS.map((group) => {
            const rows = usage.consumers.filter((c) => c.kind === group.kind);
            if (rows.length === 0) return null;
            return (
              <div key={group.kind} data-testid={`field-usage-group-${group.kind}`}>
                <div
                  style={{
                    padding: '5px 10px 3px',
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted, #6b7280)',
                  }}
                >
                  {group.label}
                </div>
                {rows.map((c, i) => (
                  <div
                    key={`${c.name}-${c.detail}-${i}`}
                    data-testid="field-usage-row"
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 6,
                      padding: '4px 10px',
                      // A neutral translucent stripe — visible on the white
                      // card (and any theme) where a themed --surface token
                      // would collapse to the card colour. Sharon 2026-07-17.
                      background: i % 2 === 1 ? 'rgba(15, 23, 42, 0.05)' : 'transparent',
                    }}
                  >
                    <span style={{ color: 'var(--text, #111827)', fontWeight: 400 }}>{c.name}</span>
                    <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: 10 }}>
                      ({c.detail})
                    </span>
                    {c.required && <RequiredBadge />}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          data-testid="field-usage-empty"
          style={{ flex: '0 0 auto', padding: '8px 10px', color: 'var(--text, #111827)' }}
        >
          Not currently used by any form or template.
        </div>
      )}
      <div
        data-testid="field-usage-coverage"
        style={{
          flexShrink: 0,
          borderTop: '1px solid var(--border, #e5e7eb)',
          padding: '6px 10px',
          fontSize: 10,
          lineHeight: 1.45,
          color: 'var(--text-muted, #6b7280)',
        }}
      >
        {/* phase-regional-contacts-in-generation — per-field role note
            (regional resolution / fallback role / mapped-but-unconsumed)
            rendered with the coverage footer. */}
        {usage?.note && (
          <div data-testid="field-usage-note" style={{ marginBottom: 4 }}>
            {usage.note}
          </div>
        )}
        {coverage}
      </div>
    </>
  );
}

export function FieldUsagePopover({
  column,
  label,
  usage,
  coverage,
}: {
  column: string;
  label: string;
  usage: CompanyProfileFieldUsage | undefined;
  coverage: string;
}): ReactElement {
  const [pinned, setPinned] = useState(false); // toggled by click / keyboard
  const [hovered, setHovered] = useState(false); // hover or focus
  const wrapRef = useRef<HTMLSpanElement>(null);
  const visible = pinned || hovered;
  // The popover element is the aria-describedby target; its rows read as a
  // sensible flattened string for assistive tech (name, variable, REQUIRED…).
  const popoverId = `field-usage-popover-${column}`;

  useEffect(() => {
    if (!pinned) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        // Fully dismiss — clear hover too, so ESC closes even while the
        // pointer still rests on the icon (keyboard users and hover-then-ESC).
        setPinned(false);
        setHovered(false);
      }
    };
    const onDown = (e: Event): void => {
      const target = e.target as HTMLElement | null;
      if (wrapRef.current && target && !wrapRef.current.contains(target)) setPinned(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [pinned]);

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', marginLeft: 5 }}>
      <button
        type="button"
        data-testid={`field-info-${column}`}
        aria-label={`Where ${label} is used`}
        aria-expanded={visible}
        {...(visible ? { 'aria-describedby': popoverId } : {})}
        // preventDefault on the pointer + click keeps the parent <label> from
        // stealing focus into the wrapped input (the reported symptom).
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setPinned((v) => !v);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          color: 'var(--accent)',
          lineHeight: 0,
        }}
      >
        <InfoGlyph />
      </button>
      {visible && (
        <span
          id={popoverId}
          role="tooltip"
          data-testid={`field-usage-popover-${column}`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 1000, // above the modal body (dialog is zIndex 300)
            width: 300,
            minWidth: 280,
            maxWidth: 320,
            maxHeight: 320, // long lists scroll inside rather than off-screen
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'var(--card, #ffffff)',
            color: 'var(--text, #111827)',
            border: '1px solid var(--border, #d0d5dd)',
            borderRadius: 6,
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.22)',
            fontSize: 11,
            lineHeight: 1.5,
            textAlign: 'left',
            cursor: 'default',
            fontWeight: 400,
          }}
        >
          <UsageContent usage={usage} coverage={coverage} />
        </span>
      )}
    </span>
  );
}

export function Field({
  label,
  column,
  children,
  htmlFor,
}: {
  label: string;
  /** CompanyProfiles column name — enables the ⓘ usage popover. */
  column?: string;
  children: ReactElement;
  /**
   * phase-ui-review-F12 — explicit control id. Supply it when `children` is a
   * fragment (more than one node, e.g. an input plus a helper line) so the
   * label can still be associated; for a lone control the id is injected
   * automatically and this can be omitted.
   */
  htmlFor?: string;
}): ReactElement {
  const usageMap = useContext(FieldUsageContext);
  const autoId = useId();
  // phase-ui-review-F12 — the inputs read as unlabelled: the control was only
  // implicitly wrapped by the <label>, and that <label> also wrapped the
  // interactive ⓘ usage button, which polluted the computed accessible name.
  // Associate the label with its control EXPLICITLY via htmlFor/id, and render
  // the ⓘ button as a sibling of the <label>, not a child of it.
  const existingId =
    isValidElement(children) ? (children.props as { id?: string }).id : undefined;
  const controlId = htmlFor ?? existingId ?? autoId;
  const control =
    htmlFor === undefined &&
    existingId === undefined &&
    isValidElement(children) &&
    children.type !== Fragment
      ? cloneElement(children, { id: controlId } as { id: string })
      : children;
  return (
    <div style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>
      <span style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <label htmlFor={controlId}>{label}</label>
        {column && usageMap && (
          <FieldUsagePopover
            column={column}
            label={label}
            usage={usageMap.fields[column]}
            coverage={usageMap.coverage}
          />
        )}
      </span>
      {control}
    </div>
  );
}
